/**
 * Landlord Verification case page — /staff-ops/landlord/:sid
 *
 * Full parity with the Employment Verification workspace. Same operational
 * template: Claimed Tenancy (editable) → Verification Attempts → Structured
 * Findings → Supporting Document Review → Internal Report → PM Summary
 * publish + preview. Uses the shared `VerifiedReport` component so the PM
 * report is identical in shape to Employment.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { apiClient, formatApiError } from "@/lib/api";
import { fmtDateLike } from "@/lib/dateFormat";
import CopyVerificationDialog from "@/components/CopyVerificationDialog";
import AppShell from "@/components/AppShell";
import { VerificationEntriesPanel } from "@/components/VerificationEntries";
import VerificationWorkspaceShell from "@/components/VerificationWorkspaceShell";
import VerifiedReport from "@/components/VerifiedReport";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Home, CheckCircle2, FileText, Save, ClipboardCheck, Send, Undo2, ShieldAlert } from "lucide-react";
import ManualRiskSelector from "@/components/ManualRiskSelector";
import { DashboardSkeleton } from "@/components/Skeleton";
import { fmtDateTime } from "@/lib/dateFormat";

// ── Vocabularies ─────────────────────────────────────────────────────────
const LEASE_TYPES = [
  { v: "month_to_month", l: "Month-to-Month" },
  { v: "fixed_term", l: "Fixed Term" },
  { v: "sublease", l: "Sublease" },
  { v: "corporate", l: "Corporate" },
  { v: "other", l: "Other" },
];
const OCCUPANCY_STATUS = [{ v: "current", l: "Current" }, { v: "previous", l: "Previous" }];
const YES_NO_UNABLE = [{ v: "yes", l: "Yes" }, { v: "no", l: "No" }, { v: "unable", l: "Unable to Verify" }];
const YES_NO = [{ v: "yes", l: "Yes" }, { v: "no", l: "No" }];
const PAYMENT_HISTORY = [
  { v: "on_time", l: "On Time" },
  { v: "mostly_on_time", l: "Mostly On Time" },
  { v: "frequently_late", l: "Frequently Late" },
  { v: "unable", l: "Unable to Verify" },
];
const OUTSTANDING_BALANCE = [
  { v: "none", l: "None" },
  { v: "yes", l: "Yes" },
  { v: "unable", l: "Unable to Verify" },
];
const SECURITY_DEPOSIT = [
  { v: "returned", l: "Returned in full" },
  { v: "partial", l: "Partial" },
  { v: "not_returned", l: "Not Returned" },
  { v: "na", l: "N/A" },
];
const LEASE_VIOLATIONS = [
  { v: "none", l: "None" },
  { v: "yes", l: "Yes" },
  { v: "unable", l: "Unable to Verify" },
];
const PROPERTY_DAMAGE = [
  { v: "none", l: "None" },
  { v: "minor", l: "Minor" },
  { v: "significant", l: "Significant" },
  { v: "unable", l: "Unable to Verify" },
];
const NOTICES_TYPES = [
  { v: "pay_or_quit", l: "Pay or Quit" },
  { v: "lease_violation", l: "Lease Violation" },
  { v: "other", l: "Other" },
];
const WOULD_RENT_AGAIN = [
  { v: "yes", l: "Yes" },
  { v: "no", l: "No" },
  { v: "would_not_answer", l: "Would Not Answer" },
];
const CONSISTENCY = [
  { v: "consistent", l: "Consistent" },
  { v: "minor_variance", l: "Minor Variance" },
  { v: "inconsistent", l: "Inconsistent" },
  { v: "not_available", l: "Not Available" },
];
const OUTCOMES = [
  { v: "verified", l: "Verified — everything matches" },
  { v: "partially_verified", l: "Partially Verified" },
  { v: "unable_to_verify", l: "Unable to Verify" },
  { v: "discrepancy_identified", l: "Discrepancy Identified" },
];
const VERIFICATION_METHOD = [
  { v: "phone", l: "Phone (direct)" },
  { v: "email", l: "Email" },
  { v: "hr_portal", l: "Portal" },
  { v: "written", l: "Written" },
  { v: "third_party", l: "Third-party VOR Service" },
];

// ── Utilities ────────────────────────────────────────────────────────────
const labelOf = (arr, v) => arr.find((x) => x.v === v)?.l || (v ? v.replace(/_/g, " ") : "—");
const money = (n) => (n === null || n === undefined || n === "" ? "—"
  : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`);
const dateVariance = (a, b) => {
  if (!a || !b) return null;
  const da = new Date(a); const db = new Date(b);
  if (isNaN(da) || isNaN(db)) return null;
  const days = Math.round((db - da) / (1000 * 60 * 60 * 24));
  if (days === 0) return "Same day";
  return `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} ${days > 0 ? "later" : "earlier"}`;
};
const rentVariancePct = (c, v) => {
  const cf = Number(c); const vf = Number(v);
  if (!cf || !vf || isNaN(cf) || isNaN(vf)) return null;
  return (((vf - cf) / cf) * 100).toFixed(1);
};

// ── Claimed Tenancy (§3) ─────────────────────────────────────────────────
function ClaimedTenancyCard({ sid, claimed, canEdit, onSaved }) {
  const [form, setForm] = useState(claimed || {});
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setForm(claimed || {}); setDirty(false); }, [claimed]);
  const set = (k, v) => { setForm((prev) => ({ ...prev, [k]: v })); setDirty(true); };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        monthly_rent: form.monthly_rent ? Number(form.monthly_rent) : null,
        security_deposit: form.security_deposit ? Number(form.security_deposit) : null,
      };
      const { data } = await apiClient.put(`/staff-ops/case/${sid}/landlord/claimed-tenancy`, payload);
      toast.success("Claimed tenancy saved");
      setDirty(false);
      onSaved?.(data.claimed_tenancy);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-5 bg-white border-slate-200 space-y-4" data-testid="claimed-tenancy-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Home className="h-4 w-4 text-slate-500" />
          <div className="ntn-eyebrow text-slate-600">Claimed Rental History</div>
        </div>
        {claimed?.seeded_from_application && !claimed?.saved_at && (
          <span className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
            Auto-populated from Rental Application — review & save
          </span>
        )}
        {claimed?.saved_at && (
          <span className="text-[11px] text-slate-500">
            Saved {fmtDateTime(claimed.saved_at)}
            {claimed.saved_by_name ? ` · ${claimed.saved_by_name}` : ""}
          </span>
        )}
      </div>

      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Property Information</div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label className="text-xs">Property Address</Label>
            <Input value={form.property_address || ""} onChange={(e) => set("property_address", e.target.value)} disabled={!canEdit} data-testid="ct-address" /></div>
          <div><Label className="text-xs">Unit Number</Label>
            <Input value={form.unit_number || ""} onChange={(e) => set("unit_number", e.target.value)} disabled={!canEdit} data-testid="ct-unit" /></div>
          <div><Label className="text-xs">City</Label>
            <Input value={form.city || ""} onChange={(e) => set("city", e.target.value)} disabled={!canEdit} data-testid="ct-city" /></div>
          <div><Label className="text-xs">State</Label>
            <Input value={form.state || ""} onChange={(e) => set("state", e.target.value)} disabled={!canEdit} data-testid="ct-state" /></div>
          <div><Label className="text-xs">ZIP Code</Label>
            <Input value={form.zip_code || ""} onChange={(e) => set("zip_code", e.target.value)} disabled={!canEdit} data-testid="ct-zip" /></div>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Landlord Information</div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label className="text-xs">Landlord / Management Company</Label>
            <Input value={form.landlord_name || ""} onChange={(e) => set("landlord_name", e.target.value)} disabled={!canEdit} data-testid="ct-landlord-name" /></div>
          <div><Label className="text-xs">Contact Person</Label>
            <Input value={form.contact_person || ""} onChange={(e) => set("contact_person", e.target.value)} disabled={!canEdit} data-testid="ct-contact-person" /></div>
          <div><Label className="text-xs">Phone Number</Label>
            <Input value={form.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} disabled={!canEdit} data-testid="ct-contact-phone" /></div>
          <div className="col-span-2"><Label className="text-xs">Email Address</Label>
            <Input type="email" value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} disabled={!canEdit} data-testid="ct-contact-email" /></div>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Applicant Claimed Information</div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Move-In Date</Label>
            <Input type="date" value={form.move_in_date || ""} onChange={(e) => set("move_in_date", e.target.value)} disabled={!canEdit} data-testid="ct-movein" /></div>
          <div><Label className="text-xs">Move-Out Date (leave blank if Current)</Label>
            <Input type="date" value={form.move_out_date || ""} onChange={(e) => set("move_out_date", e.target.value)} disabled={!canEdit} data-testid="ct-moveout" /></div>
          <div><Label className="text-xs">Monthly Rent</Label>
            <Input type="number" step="0.01" value={form.monthly_rent ?? ""} onChange={(e) => set("monthly_rent", e.target.value)} disabled={!canEdit} data-testid="ct-monthly-rent" /></div>
          <div><Label className="text-xs">Security Deposit</Label>
            <Input type="number" step="0.01" value={form.security_deposit ?? ""} onChange={(e) => set("security_deposit", e.target.value)} disabled={!canEdit} data-testid="ct-deposit" /></div>
          <div><Label className="text-xs">Lease Type</Label>
            <Select value={form.lease_type || ""} onValueChange={(v) => set("lease_type", v)} disabled={!canEdit}>
              <SelectTrigger data-testid="ct-lease-type"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{LEASE_TYPES.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label className="text-xs">Occupancy Status</Label>
            <Select value={form.occupancy_status || ""} onValueChange={(v) => set("occupancy_status", v)} disabled={!canEdit}>
              <SelectTrigger data-testid="ct-occupancy"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{OCCUPANCY_STATUS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="col-span-2 flex items-center gap-2">
            <Checkbox id="ct-current" checked={!!form.is_current_residence}
                      onCheckedChange={(v) => set("is_current_residence", !!v)} disabled={!canEdit} data-testid="ct-is-current" />
            <Label htmlFor="ct-current" className="text-sm text-slate-700">Applicant claims this is their current residence</Label>
          </div>
        </div>
      </div>

      <div className="flex justify-end pt-1">
        <Button onClick={save} disabled={!canEdit || busy || !dirty} className="bg-[#0A192F] text-white" data-testid="ct-save">
          <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save Claimed Tenancy"}
        </Button>
      </div>
    </Card>
  );
}

// ── Landlord Verification Questions (§5) ─────────────────────────────────
function LandlordQuestionsCard({ findings, set, canEdit, claimed }) {
  const f = findings || {};
  const c = claimed || {};
  const moveInVariance = dateVariance(c.move_in_date, f.verified_move_in_date);
  const moveOutVariance = dateVariance(c.move_out_date, f.verified_move_out_date);
  const rentVar = rentVariancePct(c.monthly_rent, f.verified_monthly_rent);
  const notices = f.notices_served || [];
  const toggleNotice = (val) => {
    if (val === "none") return set("notices_served", ["none"]);
    const next = new Set((notices || []).filter((n) => n !== "none"));
    next.has(val) ? next.delete(val) : next.add(val);
    set("notices_served", Array.from(next));
  };

  return (
    <Card className="p-5 bg-white border-slate-200 space-y-4" data-testid="landlord-questions-card">
      <div className="ntn-eyebrow text-slate-600">Landlord Verification Questions</div>

      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Residency</div>
        <div className="grid grid-cols-3 gap-3 items-center">
          <Label className="text-sm text-slate-700">Did applicant reside at this property?</Label>
          <Select value={f.resided_at_property || ""} onValueChange={(v) => set("resided_at_property", v)} disabled={!canEdit}>
            <SelectTrigger className="col-span-2" data-testid="q-resided"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{YES_NO_UNABLE.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
          <Label className="text-sm text-slate-700">Claimed Move-In</Label>
          <div className="col-span-2 text-sm text-slate-900">{fmtDateLike(c.move_in_date)}</div>
          <Label className="text-sm text-slate-700">Verified Move-In</Label>
          <Input type="date" className="col-span-2" value={f.verified_move_in_date || ""} onChange={(e) => set("verified_move_in_date", e.target.value)} disabled={!canEdit} data-testid="q-verified-movein" />
          <Label className="text-sm text-slate-700">Move-In Variance</Label>
          <div className="col-span-2 text-sm text-slate-700">{moveInVariance || "—"}</div>
          <Label className="text-sm text-slate-700">Claimed Move-Out</Label>
          <div className="col-span-2 text-sm text-slate-900">{fmtDateLike(c.move_out_date)}</div>
          <Label className="text-sm text-slate-700">Verified Move-Out</Label>
          <Input type="date" className="col-span-2" value={f.verified_move_out_date || ""} onChange={(e) => set("verified_move_out_date", e.target.value)} disabled={!canEdit} data-testid="q-verified-moveout" />
          <Label className="text-sm text-slate-700">Move-Out Variance</Label>
          <div className="col-span-2 text-sm text-slate-700">{moveOutVariance || "—"}</div>
          <Label className="text-sm text-slate-700">Current Resident?</Label>
          <Select value={f.still_current_resident || ""} onValueChange={(v) => set("still_current_resident", v)} disabled={!canEdit}>
            <SelectTrigger className="col-span-2" data-testid="q-still-current"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{YES_NO.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Financial</div>
        <div className="grid grid-cols-3 gap-3 items-center">
          <Label className="text-sm text-slate-700">Claimed Monthly Rent</Label>
          <div className="col-span-2 text-sm text-slate-900">{money(c.monthly_rent)}</div>
          <Label className="text-sm text-slate-700">Verified Monthly Rent</Label>
          <Input type="number" step="0.01" className="col-span-2" value={f.verified_monthly_rent ?? ""} onChange={(e) => set("verified_monthly_rent", e.target.value)} disabled={!canEdit} data-testid="q-verified-rent" />
          <Label className="text-sm text-slate-700">Rent Variance</Label>
          <div className="col-span-2 text-sm text-slate-700">{rentVar !== null ? `${rentVar}%` : "—"}</div>
          <Label className="text-sm text-slate-700">Payment History</Label>
          <Select value={f.payment_history || ""} onValueChange={(v) => set("payment_history", v)} disabled={!canEdit}>
            <SelectTrigger className="col-span-2" data-testid="q-payment-history"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{PAYMENT_HISTORY.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
          {/* # of Late Payments — 2026-02: numeric whole-number, min 0.
              Blank remains distinguishable from 0 so unknown ≠ zero. */}
          <Label className="text-sm text-slate-700" htmlFor="q-num-late-payments"># of Late Payments</Label>
          <Input
            id="q-num-late-payments"
            type="number"
            min={0}
            step={1}
            className="col-span-2"
            placeholder="Unknown"
            value={f.number_of_late_payments ?? ""}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") { set("number_of_late_payments", null); return; }
              const n = parseInt(raw, 10);
              if (Number.isFinite(n) && n >= 0) set("number_of_late_payments", n);
            }}
            disabled={!canEdit}
            data-testid="q-num-late-payments"
          />
          <Label className="text-sm text-slate-700">Outstanding Balance</Label>
          <Select value={f.outstanding_balance || ""} onValueChange={(v) => set("outstanding_balance", v)} disabled={!canEdit}>
            <SelectTrigger className="col-span-2" data-testid="q-outstanding"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{OUTSTANDING_BALANCE.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
          {f.outstanding_balance === "yes" && (
            <>
              <Label className="text-sm text-slate-700">Balance Amount</Label>
              <Input type="number" step="0.01" className="col-span-2" value={f.outstanding_balance_amount ?? ""} onChange={(e) => set("outstanding_balance_amount", e.target.value)} disabled={!canEdit} data-testid="q-outstanding-amount" />
            </>
          )}
          <Label className="text-sm text-slate-700">Security Deposit</Label>
          <Select value={f.security_deposit_status || ""} onValueChange={(v) => set("security_deposit_status", v)} disabled={!canEdit}>
            <SelectTrigger className="col-span-2" data-testid="q-security-deposit"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{SECURITY_DEPOSIT.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Lease Performance</div>
        <div className="grid grid-cols-3 gap-3 items-center">
          <Label className="text-sm text-slate-700">Lease Violations</Label>
          <Select value={f.lease_violations || ""} onValueChange={(v) => set("lease_violations", v)} disabled={!canEdit}>
            <SelectTrigger className="col-span-2" data-testid="q-lease-violations"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{LEASE_VIOLATIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
          {f.lease_violations === "yes" && (
            <>
              <Label className="text-sm text-slate-700">Explanation</Label>
              <Textarea rows={2} className="col-span-2" value={f.lease_violations_detail || ""} onChange={(e) => set("lease_violations_detail", e.target.value)} disabled={!canEdit} data-testid="q-lease-violations-detail" />
            </>
          )}
          <Label className="text-sm text-slate-700">Property Damage Beyond Normal Wear</Label>
          <Select value={f.property_damage || ""} onValueChange={(v) => set("property_damage", v)} disabled={!canEdit}>
            <SelectTrigger className="col-span-2" data-testid="q-property-damage"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{PROPERTY_DAMAGE.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
          <Label className="text-sm text-slate-700">Notices Served</Label>
          <div className="col-span-2 flex flex-wrap gap-3">
            <div className="flex items-center gap-1.5">
              <Checkbox id="notice-none" checked={notices.length === 1 && notices[0] === "none"}
                        onCheckedChange={() => toggleNotice("none")} disabled={!canEdit} data-testid="q-notice-none" />
              <Label htmlFor="notice-none" className="text-xs">None</Label>
            </div>
            {NOTICES_TYPES.map((n) => (
              <div key={n.v} className="flex items-center gap-1.5">
                <Checkbox id={`notice-${n.v}`} checked={notices.includes(n.v)}
                          onCheckedChange={() => toggleNotice(n.v)} disabled={!canEdit} data-testid={`q-notice-${n.v}`} />
                <Label htmlFor={`notice-${n.v}`} className="text-xs">{n.l}</Label>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Overall Tenancy Standing</div>
        <div className="grid grid-cols-3 gap-3 items-center">
          <Label className="text-sm text-slate-700">Would landlord rent to applicant again?</Label>
          <Select value={f.would_rent_again || ""} onValueChange={(v) => set("would_rent_again", v)} disabled={!canEdit}>
            <SelectTrigger className="col-span-2" data-testid="q-would-rent-again"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{WOULD_RENT_AGAIN.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
          {f.would_rent_again === "no" && (
            <>
              <Label className="text-sm text-slate-700">Explanation</Label>
              <Textarea rows={2} className="col-span-2" value={f.would_rent_again_reason || ""} onChange={(e) => set("would_rent_again_reason", e.target.value)} disabled={!canEdit} data-testid="q-would-rent-again-reason" />
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

// ── Supporting Document Review (§6) ─────────────────────────────────────
function LandlordDocReviewCard({ findings, set, canEdit }) {
  const f = findings.doc_review || {};
  const upd = (k, v) => set("doc_review", { ...f, [k]: v });
  const ROWS = [
    { k: "rental_app", l: "Rental Application" },
    { k: "lease_agreement", l: "Lease Agreement" },
    { k: "rental_ledger", l: "Rental Ledger" },
    { k: "notices", l: "Notices" },
    { k: "landlord_verification", l: "Landlord Verification Response" },
  ];
  return (
    <Card className="p-5 bg-white border-slate-200 space-y-3" data-testid="landlord-doc-review-card">
      <div className="ntn-eyebrow text-slate-600">Supporting Document Review</div>
      <div className="text-xs text-slate-500 -mt-1">Discrepancies feed Risk Intelligence.</div>
      <div className="space-y-2">
        {ROWS.map((row) => (
          <div key={row.k} className="grid grid-cols-3 gap-3 items-center">
            <Label className="text-sm text-slate-700">{row.l}</Label>
            <Select value={f[row.k] || ""} onValueChange={(v) => upd(row.k, v)} disabled={!canEdit}>
              <SelectTrigger className="col-span-2" data-testid={`llreview-${row.k}`}><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{CONSISTENCY.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ))}
      </div>
      <div><Label className="text-xs">Reviewer notes</Label>
        <Textarea rows={2} value={f.notes || ""} onChange={(e) => upd("notes", e.target.value)} disabled={!canEdit} data-testid="llreview-notes" /></div>
    </Card>
  );
}

// ── Verification Contact (feeds Internal Report §7) ─────────────────────
function LandlordContactCard({ findings, set, canEdit }) {
  return (
    <Card className="p-5 bg-white border-slate-200 space-y-3" data-testid="landlord-verif-contact-card">
      <div className="ntn-eyebrow text-slate-600">Verification Contact</div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Contact Name</Label>
          <Input value={findings.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)} disabled={!canEdit} data-testid="ll-verif-contact-name" /></div>
        <div><Label className="text-xs">Contact Title</Label>
          <Input value={findings.contact_title || ""} onChange={(e) => set("contact_title", e.target.value)} disabled={!canEdit} data-testid="ll-verif-contact-title" /></div>
        <div><Label className="text-xs">Phone</Label>
          <Input value={findings.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} disabled={!canEdit} data-testid="ll-verif-contact-phone" /></div>
        <div><Label className="text-xs">Verification Date</Label>
          <Input type="date" value={findings.verification_date || ""} onChange={(e) => set("verification_date", e.target.value)} disabled={!canEdit} data-testid="ll-verif-date" /></div>
        <div className="col-span-2"><Label className="text-xs">Verification Method</Label>
          <Select value={findings.method || ""} onValueChange={(v) => set("method", v)} disabled={!canEdit}>
            <SelectTrigger data-testid="ll-verif-method"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{VERIFICATION_METHOD.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="col-span-2"><Label className="text-xs">Internal Notes · NTN only</Label>
          <Textarea rows={3} value={findings.internal_notes || ""} onChange={(e) => set("internal_notes", e.target.value)} disabled={!canEdit} data-testid="ll-verif-internal-notes" /></div>
      </div>
    </Card>
  );
}

// ── Internal Verification Report preview (§7) ───────────────────────────
function InternalReportCard({ claimed, findings }) {
  const c = claimed || {};
  const f = findings || {};
  const rows = [
    { l: "Property Address", claimed: c.property_address, verified: c.property_address },
    { l: "Move-In Date", claimed: fmtDateLike(c.move_in_date), verified: fmtDateLike(f.verified_move_in_date) },
    { l: "Move-Out Date", claimed: fmtDateLike(c.move_out_date), verified: fmtDateLike(f.verified_move_out_date) },
    { l: "Monthly Rent", claimed: money(c.monthly_rent), verified: money(f.verified_monthly_rent) },
    { l: "Outstanding Balance", claimed: "—",
      verified: f.outstanding_balance === "yes"
        ? (f.outstanding_balance_amount ? `Yes — ${money(f.outstanding_balance_amount)}` : "Yes")
        : labelOf(OUTSTANDING_BALANCE, f.outstanding_balance) },
    { l: "Lease Violations", claimed: "—", verified: labelOf(LEASE_VIOLATIONS, f.lease_violations) },
    { l: "Would Rent Again", claimed: "—", verified: labelOf(WOULD_RENT_AGAIN, f.would_rent_again) },
  ];
  return (
    <Card className="p-5 bg-white border-slate-200 space-y-3" data-testid="landlord-internal-report-card">
      <div className="flex items-center gap-2">
        <ClipboardCheck className="h-4 w-4 text-slate-500" />
        <div className="ntn-eyebrow text-slate-600">Internal Verification Report (Preview)</div>
      </div>
      <div className="text-xs text-slate-500 -mt-1">Complete Landlord Verification report retained by NTN. Not shown to the Property Manager.</div>
      <div className="border border-slate-200 rounded-md overflow-hidden">
        <div className="grid grid-cols-3 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500 px-3 py-2">
          <div>Field</div><div>Claimed</div><div>Verified</div>
        </div>
        {rows.map((r) => (
          <div key={r.l} className="grid grid-cols-3 px-3 py-2 text-sm border-t border-slate-100">
            <div className="text-slate-700 font-medium">{r.l}</div>
            <div className="text-slate-900">{r.claimed || "—"}</div>
            <div className="text-slate-900">{r.verified || "—"}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-4 text-sm text-slate-700">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Verification Contact</div>
          <div>{f.contact_name || "—"}{f.contact_title ? `, ${f.contact_title}` : ""}</div>
          <div className="text-slate-500 text-xs">{f.contact_phone || ""}</div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Method / Date</div>
          <div>{labelOf(VERIFICATION_METHOD, f.method)}</div>
          <div className="text-slate-500 text-xs">{f.verification_date ? fmtDateLike(f.verification_date) : ""}</div>
        </div>
      </div>
    </Card>
  );
}

// ── PM Summary Preview + Publish (§8) ───────────────────────────────────
function PMSummaryCard({ sid, verification, canEdit, onChanged }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [riskOpen, setRiskOpen] = useState(false);
  const isCompleted = verification?.status === "completed" && !!verification?.outcome;
  const published = verification?.pm_summary;
  const publishedAt = published?.published_at;
  const manualBanner = verification?.manual_status_banner || null;

  const loadPreview = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/staff-ops/case/${sid}/landlord/summary-preview`);
      setPreview(data.preview);
    } catch (e) { /* silent */ }
  }, [sid]);
  useEffect(() => { if (isCompleted) loadPreview(); }, [isCompleted, loadPreview, verification]);

  const publish = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/staff-ops/case/${sid}/landlord/publish-summary`);
      toast.success("Property Manager summary published");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Publish failed"); }
    finally { setBusy(false); }
  };
  const unpublish = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/staff-ops/case/${sid}/landlord/unpublish-summary`);
      toast.success("Property Manager summary rescinded");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Unpublish failed"); }
    finally { setBusy(false); }
  };

  const source = published || preview;
  const reportPayload = source ? {
    ...source,
    bullets: (source.bullets || []).map((b) => ({ ...b, value: b.verified || b.claimed || "—" })),
    risk_indicators: source.risk_indicators || [],
  } : null;

  return (
    <div className="space-y-3" data-testid="pm-summary-card">
      <Card className="p-4 bg-white border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Send className="h-4 w-4 text-slate-500" />
          <div>
            <div className="ntn-eyebrow text-slate-600">Property Manager Report</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {publishedAt
                ? <>Published <span data-testid="pm-summary-published-badge">{fmtDateTime(publishedAt)}</span> · {published.published_by_name}</>
                : (isCompleted ? "Preview only — not visible to the Property Manager until published."
                               : "Complete verification to enable publish.")}
            </div>
          </div>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          {manualBanner ? (
            <span
              className={`text-[10px] uppercase tracking-[0.15em] font-bold px-2.5 py-1 rounded-sm ring-1 ${
                manualBanner.tone === "green"  ? "bg-emerald-50 text-emerald-800 ring-emerald-200" :
                manualBanner.tone === "amber"  ? "bg-amber-50 text-amber-800 ring-amber-200" :
                manualBanner.tone === "orange" ? "bg-orange-50 text-orange-800 ring-orange-200" :
                                                  "bg-rose-50 text-rose-800 ring-rose-200"
              }`}
              data-testid="manual-risk-chip"
            >{manualBanner.label}</span>
          ) : (
            <span className="text-[10px] uppercase tracking-[0.15em] font-bold px-2.5 py-1 rounded-sm ring-1 bg-slate-50 text-slate-700 ring-slate-200"
                  data-testid="manual-risk-chip">Risk not set</span>
          )}
          <Button variant="outline" onClick={() => setRiskOpen(true)} disabled={!canEdit || !isCompleted}
                  className="rounded-md" data-testid="manual-risk-btn">
            <ShieldAlert className="h-4 w-4 mr-2" /> {manualBanner ? "Edit risk" : "Set risk"}
          </Button>
          {publishedAt ? (
            <Button variant="outline" onClick={unpublish} disabled={!canEdit || busy} className="rounded-md" data-testid="pm-unpublish-btn">
              <Undo2 className="h-4 w-4 mr-2" /> Rescind
            </Button>
          ) : null}
          <Button onClick={publish} disabled={!canEdit || busy || !isCompleted || !manualBanner}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-md" data-testid="pm-publish-btn"
                  title={!manualBanner ? "Set a risk banner before publishing" : ""}>
            <Send className="h-4 w-4 mr-2" /> {publishedAt ? "Re-publish" : "Publish to PM"}
          </Button>
        </div>
      </Card>
      <ManualRiskSelector open={riskOpen} onOpenChange={setRiskOpen}
                          track="landlord" screeningId={sid} current={manualBanner}
                          onSaved={() => onChanged?.()} />
      {reportPayload ? (
        <VerifiedReport moduleName="Landlord Verification"
                        title="Verified Landlord Report" Icon={Home}
                        payload={reportPayload} testid="pm-summary-preview-report"
                        sectionLabel="Verified Rental History" />
      ) : (
        <Card className="p-6 bg-white border-slate-200 text-xs text-slate-400 italic">
          Report will appear here once the verification is completed.
        </Card>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────
export default function LandlordVerificationPage() {
  const { sid } = useParams();
  const [workspace, setWorkspace] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [findings, setFindings] = useState({});
  const [completeOpen, setCompleteOpen] = useState(false);
  const [outcome, setOutcome] = useState("verified");
  const [summary, setSummary] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, s] = await Promise.all([
        apiClient.get(`/staff-ops/case/${sid}/landlord`),
        apiClient.get(`/staff-ops/staff`),
      ]);
      setWorkspace(w.data);
      setStaff(s.data);
      setFindings(w.data.verification?.findings || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Load failed");
    } finally { setLoading(false); }
  }, [sid]);

  useEffect(() => { load(); }, [load]);
  if (loading || !workspace) return <AppShell><DashboardSkeleton /></AppShell>;

  const canEdit = workspace.can_edit || workspace.supervisor_override_available;
  const claimed = workspace.verification?.claimed_tenancy || {};
  const set = (k, v) => setFindings((prev) => ({ ...prev, [k]: v }));

  const saveDraft = async () => {
    try {
      await apiClient.post(`/staff-ops/case/${sid}/landlord/save-draft`, { findings });
      toast.success("Findings draft saved"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
  };
  const submitComplete = async () => {
    try {
      await apiClient.post(`/staff-ops/case/${sid}/landlord/complete`, { outcome, findings, summary });
      toast.success(`Verification completed · ${outcome.replace(/_/g, " ")}`);
      setCompleteOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Complete failed"); }
  };

  return (
    <AppShell>
      <VerificationWorkspaceShell track="landlord" sid={sid} workspace={workspace} staff={staff}
                                   reload={load} onSaveDraft={saveDraft}
                                   onComplete={() => setCompleteOpen(true)}>
        <ClaimedTenancyCard sid={sid} claimed={claimed} canEdit={canEdit} onSaved={load} />
        <LandlordQuestionsCard findings={findings} set={set} canEdit={canEdit} claimed={claimed} />
        <LandlordDocReviewCard findings={findings} set={set} canEdit={canEdit} />
        <LandlordContactCard findings={findings} set={set} canEdit={canEdit} />
        <InternalReportCard claimed={claimed} findings={findings} />

        {/* v1.0.10 — copy completed verification to active co-applicants */}
        {workspace.verification?.copied_from && (
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2"
               data-testid="copied-from-note">
            Copied from {workspace.verification.copied_from.source_applicant_name}&rsquo;s landlord
            verification on {fmtDateLike(workspace.verification.copied_from.copied_at?.slice(0, 10))} by{" "}
            {workspace.verification.copied_from.copied_by_name}.
          </div>
        )}
        <CopyVerificationDialog sid={sid} track="landlord"
                                sourceName={workspace.screening?.applicant_name || "this applicant"}
                                onCopied={load} />
        <PMSummaryCard sid={sid} verification={workspace.verification} canEdit={canEdit} onChanged={load} />

        {/* v1.0.8 — additional landlord/rental-history verification entries */}
        <VerificationEntriesPanel sid={sid} track="landlord" />
      </VerificationWorkspaceShell>

      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="max-w-lg" data-testid="complete-verification-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Complete verification</DialogTitle>
            <DialogDescription>Pick the overall outcome. Risk Intelligence is updated automatically from your structured findings.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Outcome</Label>
              <Select value={outcome} onValueChange={setOutcome}>
                <SelectTrigger data-testid="complete-outcome"><SelectValue /></SelectTrigger>
                <SelectContent>{OUTCOMES.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Summary (optional)</Label>
              <Textarea rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="complete-summary" />
            </div>
            <div className="text-xs text-slate-500 flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-md p-2">
              <FileText className="h-3.5 w-3.5 mt-0.5" />
              Risk Intelligence indicators are auto-generated from your structured findings on completion. The Property Manager Summary is published separately via the &quot;Publish to PM&quot; action.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteOpen(false)}>Cancel</Button>
            <Button onClick={submitComplete} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="complete-submit">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
